import React, {useState, useEffect} from 'react';
import {TextField, Button, Box, Typography, Chip, Alert} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

// Set to false to hide the US numbers not supported warning
const SHOW_US_NUMBERS_WARNING = false;

// Phone verification status constants (must match backend)
const VERIFICATION_STATUS = {
  NONE: 'none',
  VERIFYING: 'verifying',
  VERIFIED: 'verified',
  OPTED_OUT: 'opted_out',
};

// Status display configuration
const STATUS_CONFIG = {
  [VERIFICATION_STATUS.NONE]: {
    label: 'Not Verified',
    color: 'default',
    icon: RemoveCircleOutlineIcon,
    textColor: '#757575',
  },
  [VERIFICATION_STATUS.VERIFYING]: {
    label: 'Verifying',
    color: 'warning',
    icon: HourglassEmptyIcon,
    textColor: '#ed6c02',
  },
  [VERIFICATION_STATUS.VERIFIED]: {
    label: 'Phone Verified',
    color: 'success',
    icon: CheckCircleIcon,
    textColor: '#2e7d32',
  },
  [VERIFICATION_STATUS.OPTED_OUT]: {
    label: 'Opted-out',
    color: 'error',
    icon: PhoneDisabledIcon,
    textColor: '#d32f2f',
  },
};

/**
 *
 * @param {string} phoneNumber
 * @return {*}
 */
function validatePhoneNumber(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return false;
  }
  // E.164 format: +[country code][number], 8-15 digits total
  const e164Regex = /^\+[1-9]\d{7,14}$/;
  return e164Regex.test(phoneNumber.replace(/[\s()-]/g, ''));
}

/**
 *
 * @param {string} phoneNumber
 * @return {*}
 */
function formatPhoneForDisplay(phoneNumber) {
  if (!phoneNumber) return '';
  // Remove non-digit characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');
  // Format US numbers
  if (cleaned.startsWith('+1') && cleaned.length === 12) {
    return `+1 (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`;
  }
  return cleaned;
}

/**
 *
 * @param {string} phoneNumber
 * @return {*}
 */
function normalizePhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';
  // Remove all non-digit characters except +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');
  // Add + if missing and starts with country code
  if (!cleaned.startsWith('+') && cleaned.length >= 10) {
    // Assume US number if 10 digits
    if (cleaned.length === 10) {
      cleaned = '+1' + cleaned;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      cleaned = '+' + cleaned;
    }
  }
  return cleaned;
}

/**
 * Phone Verification Section - handles verifying a phone number for IntelliCulture SMS messages.
 * Verification is phone-level: if a phone is already verified, no SMS round-trip is needed.
 * The phone number input is managed externally (in the profile section).
 *
 * @param {string} phoneNumber - The phone number attached to the account (read-only display)
 * @param {object} verificationStatus - Current verification status (derived from phone_numbers table)
 * @param {function} onVerify - Callback when user clicks Subscribe
 * @param {boolean} disabled - Whether the section is disabled
 * @param {boolean} submitting - Whether a submission is in progress
 * @param {boolean} showGlobalOptOutWarning - Whether to show global opt-out warning
 * @return {*}
 */
function PhoneSubscriptionSection({
  phoneNumber = '',
  savedPhoneNumber = '',
  verificationStatus = null,
  onVerify,
  onSaveAndVerify,
  phoneNumberChanged = false,
  disabled = false,
  submitting = false,
  showGlobalOptOutWarning = false,
}) {
  // Get current status config
  const currentStatus = verificationStatus?.status || VERIFICATION_STATUS.NONE;
  const statusConfig = STATUS_CONFIG[currentStatus] || STATUS_CONFIG[VERIFICATION_STATUS.NONE];
  const StatusIcon = statusConfig.icon;

  // Determine if verify button should be enabled
  const hasPhoneNumber = phoneNumber && phoneNumber.trim() !== '';
  const hasSavedPhoneNumber = savedPhoneNumber && savedPhoneNumber.trim() !== '';
  const isAlreadyVerified = currentStatus === VERIFICATION_STATUS.VERIFIED;
  const canVerify = hasPhoneNumber && !disabled && !submitting && (phoneNumberChanged || !isAlreadyVerified);

  const handleVerify = () => {
    if (phoneNumberChanged && onSaveAndVerify && canVerify) {
      onSaveAndVerify(phoneNumber);
    } else if (onVerify && canVerify) {
      onVerify(phoneNumber);
    }
  };

  // Button text changes when phone number has been modified
  const buttonText = phoneNumberChanged ? 'Save and Confirm Subscribe' : 'Confirm Subscribe';

  // Format last verification time for display
  let lastActionTime;
  if (currentStatus === VERIFICATION_STATUS.OPTED_OUT && verificationStatus?.lastOptOutTime) {
    lastActionTime = `Last opt-out: ${new Date(verificationStatus.lastOptOutTime).toLocaleString()}`;
  } else if (currentStatus === VERIFICATION_STATUS.VERIFIED && verificationStatus?.lastVerifiedTime) {
    lastActionTime = `Verified: ${new Date(verificationStatus.lastVerifiedTime).toLocaleString()}`;
  } else if (currentStatus === VERIFICATION_STATUS.VERIFYING && verificationStatus?.lastVerificationRequestTime) {
    lastActionTime = `Verification sent: ${new Date(verificationStatus.lastVerificationRequestTime).toLocaleString()}`;
  }

  return (
    <Box sx={{p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa', textAlign: 'left'}}>
      <Typography variant='h6' sx={{mb: 2, fontWeight: 'bold', color: 'black', textAlign: 'left'}}>
        Phone Subscription for IntelliCulture Messages
      </Typography>

      {/* Explanation */}
      <Box sx={{mb: 2, textAlign: 'left'}}>
        <Typography variant='body2' sx={{color: '#424242', lineHeight: 1.6}}>
          Subscribing your phone enables SMS notifications from IntelliCulture, such as alert notifications. If this
          phone number has already been verified, no additional verification is needed. Otherwise, a verification text
          will be sent.
        </Typography>
      </Box>

      {/* US Numbers Not Supported Warning */}
      {SHOW_US_NUMBERS_WARNING && (
        <Alert severity='warning' sx={{mb: 2}}>
          US phone numbers are not supported yet. Please use a non-US number.
        </Alert>
      )}

      {/* Phone Number Display */}
      <Box sx={{mb: 2, textAlign: 'left'}}>
        <Typography variant='body2' sx={{mb: 0.5, fontWeight: 500, textAlign: 'left'}}>
          Account Phone Number:
        </Typography>
        <Typography variant='body2' sx={{color: hasSavedPhoneNumber || phoneNumberChanged ? '#212121' : '#9e9e9e'}}>
          {phoneNumberChanged
            ? `${hasSavedPhoneNumber ? formatPhoneForDisplay(savedPhoneNumber) : 'None'} → ${formatPhoneForDisplay(
                phoneNumber
              )}`
            : hasSavedPhoneNumber
            ? formatPhoneForDisplay(savedPhoneNumber)
            : 'No phone number set — add one in the profile above and save.'}
        </Typography>
      </Box>

      {/* Status Display */}
      <Box sx={{mb: 2, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start'}}>
        <Typography variant='body2' sx={{fontWeight: 500}}>
          Verification Status:
        </Typography>
        <Chip
          icon={<StatusIcon sx={{fontSize: 18}} />}
          label={statusConfig.label}
          color={statusConfig.color}
          size='small'
          variant='outlined'
        />
      </Box>

      {/* Last Action Time */}
      {lastActionTime && (
        <Box sx={{mb: 2, textAlign: 'left'}}>
          <Typography variant='caption' sx={{color: '#757575'}}>
            {lastActionTime}
          </Typography>
        </Box>
      )}

      {/* Global Opt-Out Warning */}
      {showGlobalOptOutWarning && (
        <Alert severity='error' sx={{mb: 2}}>
          This phone number has been globally opted out. The user must text <strong>START</strong> to the IntelliCulture
          number to resubscribe before it can be verified.
        </Alert>
      )}

      {/* Disclaimer */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
          textAlign: 'left',
        }}
      >
        <Typography variant='caption' sx={{color: '#616161', lineHeight: 1.5}}>
          By subscribing, you agree to receive transactional alerts. Msg frequency varies. Msg & data rates may apply.
          You can opt out at any time by replying STOP to any SMS message. Reply HELP for help.{' '}
          <a
            href='https://intelliculture.com/terms-full/'
            target='_blank'
            rel='noopener noreferrer'
            style={{color: '#1976d2'}}
          >
            Terms
          </a>{' '}
          |{' '}
          <a
            href='https://intelliculture.com/privacy-policy/'
            target='_blank'
            rel='noopener noreferrer'
            style={{color: '#1976d2'}}
          >
            Privacy
          </a>
          .
        </Typography>
      </Box>

      {/* Action Button */}
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start'}}>
        <Button variant='ic-button' color='success' onClick={handleVerify} disabled={!canVerify} size='small'>
          {buttonText}
        </Button>
      </Box>
    </Box>
  );
}

export {
  PhoneSubscriptionSection,
  VERIFICATION_STATUS,
  STATUS_CONFIG,
  validatePhoneNumber,
  normalizePhoneNumber,
  formatPhoneForDisplay,
};
